import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import EChainMarkdownFactory from '../../markdown/EChainMarkdownFactory'
import {interpolateEdgeDetails} from "../../markdown/interpolation";
import CompoundComparisonWithMCS from './CompoundComparisonWithMCS';

const SingleChain = ({
      path = [],
      scaleFactor,
      actions,
      datasets,
      highlightedDatasets,
      hideFirstLast = false,
      gridColumns = 9,
      activeComparisonEdgeIdx = null,
      onComparisonToggle = null,
      groupHeaderEntityRef = null,
      fixedHeaderEntityRef = null,
      chainIdx = 0,
      isLastInGroup = false
  }) => {

  const chain = [...path];
  const edgeSimilarities = {}; // Track which edges have similarity
  const localEntityRefs = useRef({}); // Local refs for entities in this chain
  const [closingComparisonIdx, setClosingComparisonIdx] = React.useState(null); // Track which comparison is closing

  for (let idx=1; idx + 1 < chain.length; idx += 2) {
      const endPoints = [chain[idx-1].entity, chain[idx+1].entity];
      // Enable comparison if both entities are in the same category
      // This allows structure comparison across any similarity type (sim, ecfp4, etc.)
      const canCompare = endPoints[0]?.baseCategory &&
                        endPoints[0].baseCategory === endPoints[1]?.baseCategory;

      if (canCompare) {
        chain[idx-1].similarNeighbor = endPoints[1];
        chain[idx+1].similarNeighbor = endPoints[0];
        // Store similarity info for this edge
        edgeSimilarities[idx] = {
          entity1: endPoints[0],
          entity2: endPoints[1]
        };
      }
      chain[idx] = {
          ...path[idx],
          content: path[idx].content.map(edge => ({
            ...edge,
            dataPoints: edge.dataPoints
              .map(dp => interpolateEdgeDetails(dp, datasets[edge.dataset] || {},
                                                endPoints[0].baseCategory,
                                                endPoints[1].baseCategory,
                                                datasets)),
          })),
      };
  }

  // Store ref to CompoundComparison's handleClose so we can trigger it
  const comparisonHandleCloseRef = useRef(null);

  // Enhanced actions with comparison callback that handles closing animation
  const handleComparisonClick = (edgeIdx) => {
    // If clicking the same edge that's already open, trigger close animation via hook
    if (edgeIdx === activeComparisonEdgeIdx) {
      if (comparisonHandleCloseRef.current) {
        comparisonHandleCloseRef.current();
      }
    } else if (activeComparisonEdgeIdx !== null) {
      // Switching to a different comparison - trigger close animation, then open new
      if (comparisonHandleCloseRef.current) {
        comparisonHandleCloseRef.current();
      }
      // Schedule opening new comparison after close completes
      setTimeout(() => {
        if (onComparisonToggle) {
          onComparisonToggle(edgeIdx); // Open new
        }
      }, 450); // Slightly longer than animation
    } else {
      // Opening a new comparison with none currently open
      if (onComparisonToggle) {
        onComparisonToggle(edgeIdx);
      }
    }
  };

  const enhancedActions = actions ? {
    ...actions,
    onComparisonClick: handleComparisonClick
  } : {
    onComparisonClick: handleComparisonClick
  };

  // Hide first and last nodes if requested (they're shown in group header)
  const displayChain = hideFirstLast ? chain.slice(1, -1) : chain;
  const indexOffset = hideFirstLast ? 1 : 0; // Offset to map displayChain index back to chain index

  // Calculate number of nodes in full chain (for grid positioning)
  const nodeCount = chain.filter(el => el.entity).length;

  // Calculate edge column span based on chain length
  // 2 nodes → edges span 3, 3 nodes → edges span 2, 5 nodes → edges span 1
  const edgeSpan = nodeCount === 2 ? 3 : nodeCount === 3 ? 2 : 1;

  // Calculate grid column start position to center the chain
  const visibleNodes = hideFirstLast ? nodeCount - 2 : nodeCount;
  const visibleEdges = visibleNodes + 1;
  const totalColumnsUsed = visibleNodes + (visibleEdges * edgeSpan);
  const startColumn = Math.floor((gridColumns - totalColumnsUsed) / 2) + 1;
  const endColumn = startColumn + totalColumnsUsed;

  const hasActiveComparison = activeComparisonEdgeIdx !== null;

  // Get refs for comparison
  const getComparisonRefs = (edgeIdx) => {
    // Edge index points to edge, entities are at edgeIdx-1 and edgeIdx+1
    const entity1Idx = edgeIdx - 1;
    const entity2Idx = edgeIdx + 1;

    // entity1 (LHS of edge): use groupHeaderEntityRef if idx 0, else local ref
    const sourceRef1 = entity1Idx === 0 ? groupHeaderEntityRef : localEntityRefs.current[entity1Idx];

    // entity2 (RHS of edge): use local ref if available, else fixedHeaderEntityRef
    const sourceRef2 = localEntityRefs.current[entity2Idx] || fixedHeaderEntityRef;

    return { sourceRef1, sourceRef2 };
  };

  // Calculate actual grid columns needed based on displayChain content
  // Count visible nodes and edges, accounting for edge span
  const visibleNodesInDisplay = displayChain.filter(el => el.entity).length;
  const visibleEdgesInDisplay = displayChain.filter(el => el.type === 'multiedge').length;
  const gridColumnsNeeded = visibleNodesInDisplay + (visibleEdgesInDisplay * edgeSpan);
  const gridClass = `grid-${gridColumnsNeeded}`;

  return (
    <>
        {/* Left timeline connector - positioned in start-node column */}
        <div className="ev-ch-timeline-connector" style={{gridColumn: 'start-node'}}>
          <div className={`ev-ch-timeline-line ${isLastInGroup ? 'last-row' : ''}`} />
          <div className="ev-ch-timeline-circle" />
        </div>

        {/* Chain content wrapper - subdivides content column */}
        <div className={`ev-ch-chain-row ${gridClass}`}>
        {
          displayChain.map((el, idx) => {
            // Map displayChain index back to original chain index for edgeSimilarities lookup
            const originalIdx = idx + indexOffset;

            // Determine edge position for justification
            // Only nudge edges when chain has multiple visible elements (not solitary)
            let edgePosition = 'center';
            if (el.type === 'multiedge' && displayChain.length > 1) {
              if (idx === 0) {
                edgePosition = 'left';  // First edge - justify right toward center
              } else if (idx === displayChain.length - 1) {
                edgePosition = 'right'; // Last edge - justify left toward center
              }
            }

            return (
              <EChainMarkdownFactory
                  key={idx}
                  highlightedDatasets={highlightedDatasets}
                  data={el}
                  scaleFactor={scaleFactor}
                  actions={enhancedActions}
                  edgeIndex={originalIdx}
                  hasSimilarity={!!edgeSimilarities[originalIdx]}
                  edgeSpan={edgeSpan}
                  edgePosition={edgePosition}
                  entityRef={el.entity ? (() => {
                    // Create a ref and store it in localEntityRefs by position
                    if (!localEntityRefs.current[originalIdx]) {
                      localEntityRefs.current[originalIdx] = { current: null };
                    }
                    return localEntityRefs.current[originalIdx];
                  })() : null}
              />
            );
          })
        }
        </div>

        {/* Right timeline connector - positioned in end-node column */}
        <div className="ev-ch-timeline-connector" style={{gridColumn: 'end-node'}}>
          <div className={`ev-ch-timeline-line ${isLastInGroup ? 'last-row' : ''}`} />
          <div className="ev-ch-timeline-circle" />
        </div>

        {/* Row separator - spans content column only, not rendered for last chain in group */}
        {!isLastInGroup && (
          <div className="ev-ch-row-separator" />
        )}

        {/* Comparison component */}
        {hasActiveComparison && (() => {
          const { sourceRef1, sourceRef2 } = getComparisonRefs(activeComparisonEdgeIdx);
          const entity1Idx = activeComparisonEdgeIdx - 1;
          const entity2Idx = activeComparisonEdgeIdx + 1;
          const entity1 = chain[entity1Idx]?.entity;
          const entity2 = chain[entity2Idx]?.entity;

          if (!entity1 || !entity2) return null;

          // onClose from the close button - the hook will delay this call for 400ms
          const handleCloseFromButton = () => {
            // Now actually close the comparison
            if (onComparisonToggle) {
              onComparisonToggle(activeComparisonEdgeIdx);
            }
          };

          return (
            <div className="ev-ch-comparator-wrapper" style={{gridColumn: '1 / -1'}}>
              <CompoundComparisonWithMCS
                entity1={entity1}
                entity2={entity2}
                datasets={datasets}
                onClose={handleCloseFromButton}
                sourceRef1={sourceRef1}
                sourceRef2={sourceRef2}
                handleCloseRef={comparisonHandleCloseRef}
              />
            </div>
          );
        })()}
    </>
  )
}

SingleChain.propTypes = {
  path: PropTypes.arrayOf(PropTypes.object).isRequired,
  scaleFactor: PropTypes.string,
  actions: PropTypes.object,
  datasets: PropTypes.object,
  highlightedDatasets: PropTypes.arrayOf(PropTypes.string),
  hideFirstLast: PropTypes.bool,
  gridColumns: PropTypes.number,
  activeComparisonEdgeIdx: PropTypes.number,
  onComparisonToggle: PropTypes.func,
  groupHeaderEntityRef: PropTypes.object,
  fixedHeaderEntityRef: PropTypes.object,
  chainIdx: PropTypes.number,
  isLastInGroup: PropTypes.bool
}

export default SingleChain;
